import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { UserRole, dbCols } from '@cc/schema';
import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Modal, Space, Table, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useNavigation,
  useTranslate,
} from '@refinedev/core';
import { useActor } from '@xstate/react';
import { format, formatDistance } from 'date-fns';
import { dateFnsLocaleFor } from '../../utils/dateFormat';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TherapistActivityReactContext } from '../../App';
import { useSubscriptionWarnings } from '../../hooks';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientOptions } from '../../hooks/client-options';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';
import { Session } from '../../schema/be-graphql-generated';
import { colors } from '../../styles/colors';
import useStyles from './styles.style';

export type UiEventType = {
  id: string;
  start: Date | null;
  duration?: {
    minutes: number;
  };
  title: string;
  hasEnd: boolean;
  session?: Session;
};

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const SessionsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const t = useTranslate();
  // F-b19 (founder 2026-09-21: "why not capture more initially?"): the calendar is a place a
  // therapist reviews a client from, so its time counts as activity on the filtered client —
  // an RTM filter can leave it out later; the review-session idle timer sees it now
  useActivityTracking(dbCols.sessionsRelations, fixedClientId, !fixedClientId);

  const { styles } = useStyles();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;

  const locale = useGetLocale();

  const currentLocale = locale();

  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s

  const names = useClientNamesVisibility();

  const { briefClientInfo } = useClientOptions();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const {
    tableProps,
    tableQueryResult,
    sorters,
    searchFormProps,
    filters,
    setFilters,
  } = useTable<Session>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'sessions',
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      operation: 'sessions',
      fields: [
        {
          data: ['_id', '_to', 'createdAt', 'updatedAt', 'finishedAt'],
        },
        'count',
      ],
    },

    pagination: {
      pageSize: 500,
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],

      'replace',
    );
  }, [clientFilterId]);

  const subWarnings = useSubscriptionWarnings();

  const { push } = useNavigation();

  const isCreateButtonDisabled =
    subWarnings?.limitReached || subWarnings?.subscriptionExpired;

  const [showNeedUpgrade, setShowNeedUpgrade] = useState(false);

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={t('pages.sessions.title')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-sessions-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-session-button"
            onClick={() => {
              isCreateButtonDisabled
                ? setShowNeedUpgrade(true)
                : push(
                    fixedClientId
                      ? `/sessions/create?clientId=${encodeURIComponent(fixedClientId)}`
                      : '/sessions/create',
                  );
            }}
          />
        </div>
      }
    >
      <div className={styles['session--tabs-row']} data-testid="sessions-list">
        <Table
          {...embeddedTableProps(fixedClientId)}
          {...tableProps}
          data-testid="sessions-list-table"
          rowKey="_id"
          onChange={(pagination, filters, sorter, extra) => {
            // Note: The data we get from Ant.design filters does not match the
            // react refine one. Currently it works fine if I simply silence it the way below.
            // TOOD: Find a way to convert the data to match the react refine one and fix it better
            // @ts-ignore
            tableProps?.onChange?.(pagination, null, sorter, extra);
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
          }}
        >
          {fixedClientId ? null : (
          <Table.Column
            key="_to"
            dataIndex="_to"
            title={t('resourcesCommon.userId')}
            sorter
            filterSearch
            // @ts-ignore
            onFilter={(value: string, record: DownwardArrow) => {
              return record._to === value;
            }}
            filters={briefClientInfo.map((c, idx) => {
              return {
                text: clientFilterText(c, names.visible),
                value: c._id,
              };
            })}
            render={(value, record) => {
              const client = briefClientInfo.find?.((c) => c._id === value);

              return (
                <ClientName
                  id={value}
                  firstName={client?.firstName}
                  lastName={client?.lastName}
                  visible={names.visible}
                />
              );
            }}
          />
          )}

          <Table.Column
            key="createdAt"
            dataIndex="createdAt"
            title={t('resourcesCommon.createdAt')}
            sorter
            render={(text, record, indx) => {
              if (!text) return undefined;
              const date = new Date(text);
              return format(
                date,
                currentLocale?.startsWith('ru')
                  ? 'yyyy-MM-dd HH:mm'
                  : 'yyyy-MM-dd hh:mm aaa',
              );
            }}
          />

          <Table.Column
            key="finishedAt"
            dataIndex="finishedAt"
            title={t('resourcesCommon.finishedAt')}
            sorter
            render={(text, record, indx) => {
              if (!text) return undefined;
              const date = new Date(text);
              return format(
                date,
                currentLocale?.startsWith('ru')
                  ? 'yyyy-MM-dd HH:mm'
                  : 'yyyy-MM-dd hh:mm aaa',
              );
            }}
          />

          <Table.Column<Session>
            key="createdAt"
            dataIndex="createdAt"
            title={t('resourcesCommon.duration')}
            sorter
            render={(text, record, indx) => {
              return record.finishedAt
                ? formatDistance(
                    new Date(record.createdAt),
                    new Date(record.finishedAt),
                    { locale: dateFnsLocaleFor(currentLocale) },
                  )
                : t('pages.sessions.noFinishDate');
            }}
          />

          <Table.Column<Session>
            title={t('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record) => {
              const client = briefClientInfo.find((c) => c._id === record._to);

              const hasPremiumAccess = client?.accessLevel;

              const canEdit =
                client?.access?.allowEdit ||
                (client?.access?.allowAdminEdit &&
                  identity?.db?.role === UserRole.THERAPIST_ADMIN);

              return (
                <Space>
                  {canEdit && (
                    <Tooltip
                      title={t(
                        hasPremiumAccess
                          ? 'resourcesCommon.edit'
                          : 'clients.thoughtsTestDisabledTip',
                      )}
                    >
                      <Link to={`/sessions/create?sessionsId=${record._id}`}>
                        <Button
                          icon={<EditOutlined />}
                          disabled={!hasPremiumAccess || !canEdit}
                        />
                      </Link>
                    </Tooltip>
                  )}
                </Space>
              );
            }}
          />
        </Table>
      </div>

      <Modal
        title={
          <>
            <ExclamationCircleOutlined
              style={{ color: colors['--primary-0'] }}
            />{' '}
            <span>{t('pages.sessions.upgradeNeeded')}</span>
          </>
        }
        cancelText={t('common.cancel')}
        okText={t('pages.sessions.gotToSubscription')}
        open={showNeedUpgrade}
        onOk={async () => {
          setShowNeedUpgrade(false);
          push('/portalSubscriptions');
        }}
        onCancel={() => {
          setShowNeedUpgrade(false);
        }}
      >
        {' '}
        {t('pages.sessions.upgradeNeededDescription')}
      </Modal>
    </ToolList>
  );
};
